import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { FiChevronRight, FiEye, FiEyeOff, FiPlus, FiX, FiPackage, FiSend, FiUsers, FiKey, FiFileText, FiLock, FiShare2, FiCopy, FiGift, FiHeadphones } from 'react-icons/fi';
import { useTelegram } from '../../hooks/useTelegram';
import { LOYALTY_LEVELS, getLevelBySpent, getNextLevel, getProgressToNext, formatMoney } from '../../config/loyalty';

const fadeIn = keyframes`from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}`;
const fillAnim = keyframes`from{width:0}`;

const Page = styled.div`
  min-height: 100vh; background: #000; padding: 16px 16px 120px;
  animation: ${fadeIn} 0.3s ease;
`;

const Header = styled.div`
  display: flex; align-items: center; gap: 14px;
  margin-bottom: 28px;
`;

const Avatar = styled.div`
  width: 52px; height: 52px; border-radius: 50%;
  background: linear-gradient(135deg, #006FFF, #0055cc);
  display: flex; align-items: center; justify-content: center;
  font-size: 22px; font-weight: 700; color: #fff;
  box-shadow: 0 0 15px rgba(0,111,255,0.3);
`;

const HeaderInfo = styled.div``;
const UserName = styled.h1`
  font-size: 22px; font-weight: 800; color: #fff; margin: 0;
  letter-spacing: -0.3px;
`;
const UserSub = styled.div`
  font-size: 13px; color: rgba(255,255,255,0.35); margin-top: 2px;
`;

const NeonCard = styled.div`
  background: rgba(0,111,255,0.03);
  border: 1px solid rgba(0,111,255,0.3);
  border-radius: 16px;
  box-shadow: 0 0 15px rgba(0,111,255,0.1);
  overflow: hidden;
  margin-bottom: 12px;
`;

const SectionLabel = styled.div`
  font-size: 13px; font-weight: 600; color: rgba(255,255,255,0.3);
  text-transform: uppercase; letter-spacing: 0.5px;
  padding: 20px 0 8px;
`;

const MenuRow = styled.a`
  display: flex; align-items: center; padding: 14px 16px;
  border-bottom: 1px solid rgba(0,111,255,0.1);
  cursor: pointer; text-decoration: none; transition: opacity 0.2s;
  &:last-child { border-bottom: none; }
  &:active { opacity: 0.6; }
`;

const MenuIconWrap = styled.div`
  width: 38px; height: 38px; border-radius: 12px;
  background: ${p => p.$bg || 'rgba(255,255,255,0.06)'};
  display: flex; align-items: center; justify-content: center;
  margin-right: 14px; flex-shrink: 0;
  color: ${p => p.$color || '#fff'};
`;

const MenuIconImg = styled.img`
  width: 71px; height: 71px; border-radius: 16px;
  margin-right: 14px; flex-shrink: 0;
  object-fit: cover;
`;

const MenuContent = styled.div`flex: 1; min-width: 0;`;
const MenuLabel = styled.div`font-size: 17px; font-weight: 600; color: #fff;`;
const MenuSub = styled.div`font-size: 14px; color: rgba(255,255,255,0.55); margin-top: 3px; line-height: 1.3;`;
const MenuArrow = styled.div`color: rgba(255,255,255,0.15); flex-shrink: 0;`;

const Badge = styled.span`
  font-size: 10px; font-weight: 700; padding: 2px 8px; border-radius: 6px; margin-left: 8px;
  background: ${p => p.$bg || 'rgba(0,200,83,0.15)'};
  color: ${p => p.$color || '#00c853'};
`;

/* Loyalty Level Images */
const LEVEL_IMAGES = {
  1: '/images/levels/bronze.png?v=3',
  2: '/images/levels/silver.png?v=2',
  3: '/images/levels/gold.png?v=2',
  4: '/images/levels/diamond.png?v=2',
  5: '/images/levels/platinum.png?v=2',
};
const LEVEL_NAMES = {
  1: 'Бронза',
  2: 'Серебро',
  3: 'Золото',
  4: 'Алмаз',
  5: 'Платина',
};
const LEVEL_CASHBACK = { 1: 1, 2: 2, 3: 5, 4: 7, 5: 10 };
const LEVEL_THRESHOLDS = [0, 10000, 35000, 70000, 150000];
const LEVEL_COLORS = {
  1: 'linear-gradient(90deg, #CD7F32, #B8860B)', // Бронза
  2: 'linear-gradient(90deg, #C0C0C0, #A8A8A8)', // Серебро
  3: 'linear-gradient(90deg, #FFD700, #FFA500)', // Золото
  4: 'linear-gradient(90deg, #00CED1, #4169E1)', // Алмаз
  5: 'linear-gradient(90deg, #E5E4E2, #B0C4DE)', // Платина
};

const LevelBadge = styled.div`
  display: flex; align-items: center; gap: 10px; padding: 18px; padding-bottom: 0;
`;

const LevelIcon = styled.div`
  width: 52px; height: 52px; border-radius: 10px;
  overflow: hidden; flex-shrink: 0;
  img { width: 100%; height: 100%; object-fit: cover; }
`;

const LevelInfo = styled.div`flex:1; margin-left: 4px;`;
const LevelName = styled.div`font-size: 16px; font-weight: 700; color: #fff;`;
const LevelXP = styled.div`font-size: 13px; color: rgba(255,255,255,0.5); margin-top: 2px;`;

const ProgressSection = styled.div`padding: 14px 18px 18px;`;

const ProgressWrap = styled.div`
  background: rgba(255,255,255,0.06); border-radius: 4px; height: 6px;
  overflow: hidden;
`;

const ProgressBar = styled.div`
  height: 100%; border-radius: 4px;
  background: ${p => p.$gradient || 'linear-gradient(90deg, #006FFF, #00c6ff)'};
  width: ${p => p.$pct}%;
  animation: ${fillAnim} 0.8s ease;
`;

const LevelHint = styled.div`
  font-size: 11px; color: rgba(255,255,255,0.3); margin-top: 8px;
`;

/* Referral */
const RefRow = styled.div`
  display: flex; align-items: center; gap: 10px; padding: 18px; padding-bottom: 12px;
`;

const RefIconWrap = styled.div`
  width: 40px; height: 40px; border-radius: 12px;
  background: linear-gradient(135deg, #006FFF, #0055cc);
  display: flex; align-items: center; justify-content: center;
  color: #fff;
`;

const RefTitle = styled.div`font-size: 16px; font-weight: 700; color: #fff;`;
const RefSub = styled.div`font-size: 12px; color: rgba(255,255,255,0.4); margin-top: 1px;`;

const RefBody = styled.div`padding: 0 18px 18px;`;

const RefCode = styled.div`
  background: rgba(0,111,255,0.06); border: 1px solid rgba(0,111,255,0.2);
  border-radius: 12px; padding: 12px 14px;
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 10px;
`;

const RefCodeText = styled.span`
  font-size: 16px; font-weight: 700; color: #006FFF; letter-spacing: 1px;
`;

const ShareBtn = styled.button`
  width: 100%; background: linear-gradient(135deg, #006FFF, #0055cc);
  border: none;
  border-radius: 14px; padding: 12px; color: #fff; font-size: 14px;
  font-weight: 700; cursor: pointer; display: flex; align-items: center;
  justify-content: center; gap: 8px;
  box-shadow: 0 0 20px rgba(0,111,255,0.3);
  transition: all 0.2s;
  &:active { transform: scale(0.97); opacity: 0.9; }
`;

const RefStats = styled.div`
  display: flex; align-items: center; gap: 6px;
  font-size: 12px; color: rgba(255,255,255,0.35); margin-top: 10px;
`;

const CopiedToast = styled.div`
  position: fixed; top: 60px; left: 50%; transform: translateX(-50%);
  background: #006FFF; color: #fff; font-size: 13px; font-weight: 600;
  padding: 8px 20px; border-radius: 20px; z-index: 999;
  animation: ${fadeIn} 0.2s ease;
`;

const ModalOverlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.85);
  z-index: 1000; display: flex; align-items: center; justify-content: center;
  padding: 20px; animation: ${fadeIn} 0.2s ease;
`;

const ModalContent = styled.div`
  background: #111; border-radius: 20px; width: 100%; max-width: 400px;
  max-height: 80vh; overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px 20px; border-bottom: 1px solid rgba(255,255,255,0.1);
`;

const ModalTitle = styled.h2`font-size: 18px; font-weight: 700; color: #fff; margin: 0;`;

const ModalClose = styled.button`
  background: none; border: none; color: rgba(255,255,255,0.5);
  font-size: 24px; cursor: pointer; padding: 0; line-height: 1;
  &:hover { color: #fff; }
`;

const ModalBody = styled.div`padding: 20px;`;

const CodeInput = styled.div`
  display: flex; gap: 10px; margin-bottom: 16px;
`;

const CodeInputField = styled.input`
  flex: 1; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1);
  border-radius: 12px; padding: 12px 16px; color: #fff; font-size: 15px;
  &::placeholder { color: rgba(255,255,255,0.3); }
  &:focus { outline: none; border-color: #006FFF; }
`;

const AddCodeBtn = styled.button`
  background: #006FFF; border: none; border-radius: 12px;
  padding: 12px 18px; color: #fff; font-weight: 600; font-size: 14px;
  cursor: pointer; white-space: nowrap;
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const CodesList = styled.div`display: flex; flex-direction: column; gap: 8px;`;

const CodeItem = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  background: rgba(255,255,255,0.05); border-radius: 10px; padding: 12px 14px;
`;

const CodeText = styled.span`font-size: 14px; color: #fff; font-family: monospace;`;

const DeleteCodeBtn = styled.button`
  background: none; border: none; color: #FF3B30; font-size: 18px;
  cursor: pointer; padding: 4px;
`;

const CodesHint = styled.div`
  font-size: 12px; color: rgba(255,255,255,0.4); margin-top: 16px;
  text-align: center;
`;

const HistoryItem = styled.div`
  display: flex; align-items: center; gap: 12px;
  background: rgba(255,255,255,0.05); border-radius: 12px; padding: 14px;
  margin-bottom: 10px;
`;

const HistoryIcon = styled.div`
  width: 44px; height: 44px; border-radius: 10px;
  background: ${p => p.$bg || 'rgba(0,111,255,0.15)'};
  display: flex; align-items: center; justify-content: center;
  font-size: 20px;
`;

const HistoryInfo = styled.div`flex: 1; min-width: 0;`;

const HistoryTitle = styled.div`
  font-size: 14px; font-weight: 600; color: #fff;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;

const HistoryDate = styled.div`font-size: 12px; color: rgba(255,255,255,0.4); margin-top: 2px;`;

const HistoryPrice = styled.div`
  font-size: 15px; font-weight: 700; color: #34C759; text-align: right;
`;

const HistoryStatus = styled.div`
  font-size: 11px; color: ${p => p.$color || 'rgba(255,255,255,0.5)'};
  text-align: right; margin-top: 2px;
`;

const EmptyHistory = styled.div`
  text-align: center; padding: 40px 20px;
  color: rgba(255,255,255,0.4); font-size: 14px;
`;

const SubOption = styled.button`
  width: 100%; background: ${p => p.$active ? 'rgba(0,111,255,0.2)' : 'rgba(255,255,255,0.05)'};
  border: 1px solid ${p => p.$active ? '#006FFF' : 'rgba(255,255,255,0.1)'};
  border-radius: 12px; padding: 14px 16px; margin-bottom: 10px;
  display: flex; align-items: center; gap: 12px; cursor: pointer;
  transition: all 0.2s;
  &:hover { background: rgba(0,111,255,0.15); }
`;

const SubIcon = styled.div`font-size: 24px;`;

const SubInfo = styled.div`flex: 1; text-align: left;`;

const SubName = styled.div`font-size: 15px; font-weight: 600; color: #fff;`;

const SubDesc = styled.div`font-size: 12px; color: rgba(255,255,255,0.5); margin-top: 2px;`;

const SubDuration = styled.div`
  display: flex; gap: 8px; margin-top: 16px;
`;

const DurationBtn = styled.button`
  flex: 1; background: ${p => p.$active ? '#006FFF' : 'rgba(255,255,255,0.05)'};
  border: 1px solid ${p => p.$active ? '#006FFF' : 'rgba(255,255,255,0.1)'};
  border-radius: 10px; padding: 10px; color: #fff; font-weight: 600;
  font-size: 13px; cursor: pointer;
`;

const ActiveSubCard = styled.div`
  background: linear-gradient(135deg, rgba(0,111,255,0.2), rgba(0,200,83,0.1));
  border: 1px solid rgba(0,111,255,0.3); border-radius: 16px; padding: 20px;
  text-align: center;
`;

const SubStatus = styled.div`
  font-size: 14px; font-weight: 600; color: ${p => p.$color || '#34C759'};
  margin-top: 8px;
`;

const CancelBtn = styled.button`
  margin-top: 16px; background: rgba(255,59,48,0.15); border: none;
  border-radius: 10px; padding: 10px 20px; color: #FF3B30;
  font-size: 13px; font-weight: 600; cursor: pointer;
`;

const STORAGE_KEY = 'openps_profile';

const haptic = () => {
  try { window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('light'); } catch {}
};

const DEFAULT_ACCOUNT = { email: '', password: '', label: '', region: 'TR' };

const migrateProfile = (raw) => {
  if (!raw) return { accounts: [{ ...DEFAULT_ACCOUNT }], console: 'ps5', region: 'TR' };
  try {
    const p = JSON.parse(raw);
    if (!p.accounts) {
      const acc = { email: p.email || '', password: p.password || '', label: '', region: p.region || 'TR' };
      return { accounts: [acc], console: p.console || 'ps5', region: p.region || 'TR' };
    }
    if (!p.region) p.region = 'TR';
    return p;
  } catch {
    return { accounts: [{ ...DEFAULT_ACCOUNT }], console: 'ps5', region: 'TR' };
  }
};

const REGIONS = [
  { code: 'TR', label: 'Турция', flag: '/icons/flag-tr.png' },
  { code: 'UA', label: 'Украина', flag: '/icons/flag-ua.png' },
  { code: 'IN', label: 'Индия', flag: '/icons/flag-in.png' },
];

const Profile = () => {
  const { tg } = useTelegram();
  const [profile, setProfile] = useState(() => migrateProfile(localStorage.getItem(STORAGE_KEY)));

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
  }, [profile]);

  // --- Loyalty & Referral ---
  const tgUserId = tg?.initDataUnsafe?.user?.id;
  const [loyalty, setLoyalty] = useState(() => {
    try { return JSON.parse(localStorage.getItem('openps_loyalty')) || null; } catch { return null; }
  });
  const [copied, setCopied] = useState(false);
  const [showCodesModal, setShowCodesModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscription, setSubscription] = useState(() => {
    try { return JSON.parse(localStorage.getItem('openps_subscription')) || null; } catch { return null; }
  });

  useEffect(() => {
    if (subscription) {
      localStorage.setItem('openps_subscription', JSON.stringify(subscription));
    } else {
      localStorage.removeItem('openps_subscription');
    }
  }, [subscription]);

  const activateSubscription = (tier, months) => {
    const now = new Date();
    const endDate = new Date(now.getTime() + months * 30 * 24 * 60 * 60 * 1000);
    setSubscription({
      tier, // essential, extra, deluxe
      months,
      startDate: now.toISOString(),
      endDate: endDate.toISOString(),
      notified: false
    });
    haptic();
    setShowSubscriptionModal(false);
  };

  const cancelSubscription = () => {
    setSubscription(null);
    haptic();
  };

  const getDaysLeft = () => {
    if (!subscription) return 0;
    const end = new Date(subscription.endDate);
    const now = new Date();
    return Math.max(0, Math.ceil((end - now) / (24 * 60 * 60 * 1000)));
  };

  const isSubscriptionActive = subscription && getDaysLeft() > 0;
  
  // Mock история покупок (в будущем из AmoCRM)
  const purchaseHistory = [
    // Пока пусто - будет заполняться из AmoCRM
  ];
  const [reserveCodes, setReserveCodes] = useState(() => {
    try { return JSON.parse(localStorage.getItem('openps_reserve_codes')) || []; } catch { return []; }
  });
  const [newCode, setNewCode] = useState('');

  useEffect(() => {
    localStorage.setItem('openps_reserve_codes', JSON.stringify(reserveCodes));
  }, [reserveCodes]);

  const addCode = () => {
    if (!newCode.trim() || reserveCodes.length >= 10) return;
    setReserveCodes([...reserveCodes, newCode.trim()]);
    setNewCode('');
    haptic();
  };

  const removeCode = (idx) => {
    setReserveCodes(reserveCodes.filter((_, i) => i !== idx));
    haptic();
  };

  useEffect(() => {
    if (!tgUserId) return;
    fetch(`/api/loyalty/${tgUserId}`).then(r => r.json()).then(d => {
      setLoyalty(d);
      localStorage.setItem('openps_loyalty', JSON.stringify(d));
    }).catch(() => {});
  }, [tgUserId]);

  // Расчёт прогресса до следующего уровня
  const level = loyalty?.level || 1;
  const currentThreshold = LEVEL_THRESHOLDS[level - 1] || 0;
  const nextThreshold = LEVEL_THRESHOLDS[level] || LEVEL_THRESHOLDS[1]; // Серебро по умолчанию
  const purchases = loyalty?.purchases || 0;
  const progressPct = loyalty?.level >= 5 
    ? 100 
    : Math.min(100, Math.round(((purchases - currentThreshold) / (nextThreshold - currentThreshold)) * 100));

  const refLink = tgUserId ? `https://t.me/rel0c_bot?start=ref_${tgUserId}` : '';

  const handleShare = useCallback(() => {
    haptic();
    if (!refLink) return;
    if (navigator.share) {
      navigator.share({ title: 'Open PS', text: 'Приведи друга — получи 300₽! 🎮', url: refLink });
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(refLink).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
    }
  }, [refLink]);

  const userName = tg?.initDataUnsafe?.user?.first_name || 'Пользователь';
  const initials = userName.charAt(0).toUpperCase();

  // Проверка рабочего времени поддержки (09:00-23:55 МСК)
  const isSupportOnline = () => {
    const now = new Date();
    const mskHours = (now.getUTCHours() + 3) % 24;
    const mskMinutes = now.getUTCMinutes();
    const mskTime = mskHours * 60 + mskMinutes;
    return mskTime >= 9 * 60 && mskTime <= 23 * 60 + 55; // 09:00 - 23:55
  };
  const supportOnline = isSupportOnline();

  return (
    <Page>
      <Header>
        <Avatar>{initials}</Avatar>
        <HeaderInfo>
          <UserName>{userName}</UserName>
          <UserSub>Open PS • Аккаунт</UserSub>
        </HeaderInfo>
      </Header>

      {copied && <CopiedToast>Ссылка скопирована!</CopiedToast>}

      {/* Уровень клиента */}
      {loyalty && (
        <>
          <SectionLabel>Уровень</SectionLabel>
          <NeonCard>
            <LevelBadge>
              <LevelIcon>
                <img src={LEVEL_IMAGES[loyalty.level] || LEVEL_IMAGES[1]} alt={LEVEL_NAMES[loyalty.level]} />
              </LevelIcon>
              <LevelInfo>
                <LevelName>{LEVEL_NAMES[loyalty.level] || 'Бронза'} <span style={{color: '#34C759', fontWeight: 600, fontSize: '14px'}}>• {LEVEL_CASHBACK[loyalty.level] || 1}% кешбэк</span></LevelName>
                <LevelXP>Покупок на {(loyalty.purchases || 0).toLocaleString('ru')} ₽</LevelXP>
              </LevelInfo>
            </LevelBadge>
            <ProgressSection>
              <ProgressWrap>
                <ProgressBar $pct={progressPct} $gradient={LEVEL_COLORS[loyalty.level] || LEVEL_COLORS[1]} />
              </ProgressWrap>
              <LevelHint>
                {(loyalty?.level || 1) < 5
                  ? `До ${LEVEL_NAMES[(loyalty?.level || 1) + 1]}: ещё ${Math.max(0, nextThreshold - purchases).toLocaleString('ru')} ₽`
                  : `Максимальный уровень • 10% кешбэк`}
              </LevelHint>
            </ProgressSection>
          </NeonCard>

          {/* Реферальная программа */}
          <SectionLabel>Приглашай друзей</SectionLabel>
          <NeonCard>
            <RefRow>
              <MenuIconImg src="/images/profile/referral.jpg?v=1" alt="Реферальная программа" style={{width: '56px', height: '56px'}} />
              <LevelInfo>
                <RefTitle>Реферальная программа</RefTitle>
                <RefSub>300₽ тебе + 300₽ другу</RefSub>
              </LevelInfo>
            </RefRow>
            <RefBody>
              <RefCode>
                <RefCodeText>{refLink || 'Загрузка...'}</RefCodeText>
                <FiCopy size={16} color="rgba(255,255,255,0.3)" style={{cursor:'pointer'}} onClick={handleShare} />
              </RefCode>
              <ShareBtn onClick={handleShare}>
                <FiShare2 size={16} /> Поделиться
              </ShareBtn>
              <RefStats>
                <FiUsers size={13} /> Приглашено: {loyalty.referrals || 0} друзей
              </RefStats>
            </RefBody>
          </NeonCard>
        </>
      )}

      {/* Настройки */}
      <SectionLabel>Настройки</SectionLabel>
      <NeonCard>
        <MenuRow as="div" onClick={() => { haptic(); setShowSubscriptionModal(true); }} style={{cursor: 'pointer'}}>
          <MenuIconImg src="/images/profile/psplus.jpg?v=2" alt="PS Plus" />
          <MenuContent>
            <MenuLabel>Активная подписка {isSubscriptionActive 
              ? <Badge $bg="rgba(0,200,83,0.15)" $color="#00c853">{getDaysLeft()} дн.</Badge>
              : <Badge $bg="rgba(255,107,53,0.15)" $color="#FF6B35">Нет</Badge>}
            </MenuLabel>
            <MenuSub>{isSubscriptionActive 
              ? `PS Plus ${subscription.tier === 'essential' ? 'Essential' : subscription.tier === 'extra' ? 'Extra' : 'Deluxe'}`
              : 'Нажмите чтобы активировать'}
            </MenuSub>
          </MenuContent>
          <MenuArrow><FiChevronRight size={20} /></MenuArrow>
        </MenuRow>
        <MenuRow as="div" onClick={() => { haptic(); setShowCodesModal(true); }} style={{cursor: 'pointer'}}>
          <MenuIconImg src="/images/profile/keys.jpg?v=2" alt="Коды" />
          <MenuContent>
            <MenuLabel>Резервные коды <Badge $bg={reserveCodes.length > 0 ? "rgba(52,199,89,0.15)" : "rgba(255,107,53,0.15)"} $color={reserveCodes.length > 0 ? "#34C759" : "#FF6B35"}>{reserveCodes.length} кодов</Badge></MenuLabel>
            <MenuSub>Для быстрого оформления заказов</MenuSub>
          </MenuContent>
          <MenuArrow><FiChevronRight size={20} /></MenuArrow>
        </MenuRow>
        <MenuRow as="div" onClick={() => { haptic(); setShowHistoryModal(true); }} style={{cursor: 'pointer'}}>
          <MenuIconImg src="/images/profile/history.jpg?v=2" alt="История" />
          <MenuContent>
            <MenuLabel>История покупок <Badge $bg="rgba(0,111,255,0.15)" $color="#006FFF">{purchaseHistory.length}</Badge></MenuLabel>
            <MenuSub>Ваши заказы и статусы</MenuSub>
          </MenuContent>
          <MenuArrow><FiChevronRight size={20} /></MenuArrow>
        </MenuRow>
      </NeonCard>

      {/* Связь */}
      <SectionLabel>Связь</SectionLabel>
      <NeonCard>
        <MenuRow href="https://t.me/openps_support" target="_blank" onClick={haptic}>
          <MenuIconImg src="/images/profile/support.jpg?v=5" alt="Поддержка" />
          <MenuContent>
            <MenuLabel>Служба поддержки {supportOnline 
              ? <Badge $bg="rgba(52,199,89,0.15)" $color="#34C759">Онлайн</Badge>
              : <Badge $bg="rgba(255,59,48,0.15)" $color="#FF3B30">Оффлайн</Badge>}
            </MenuLabel>
            <MenuSub>Отвечаем за 3 мин • 09:00–23:55</MenuSub>
          </MenuContent>
          <MenuArrow><FiChevronRight size={20} /></MenuArrow>
        </MenuRow>
        <MenuRow href="https://t.me/openps" target="_blank" onClick={haptic}>
          <MenuIconImg src="/images/profile/chat.jpg?v=2" alt="Чат" />
          <MenuContent>
            <MenuLabel>Чат геймеров</MenuLabel>
            <MenuSub>Обсуждения, советы, новости</MenuSub>
          </MenuContent>
          <MenuArrow><FiChevronRight size={20} /></MenuArrow>
        </MenuRow>
        <MenuRow href="https://t.me/open_ps" target="_blank" onClick={haptic}>
          <MenuIconImg src="/images/profile/channel.jpg?v=2" alt="Канал" />
          <MenuContent>
            <MenuLabel>Канал Open PS</MenuLabel>
            <MenuSub>Скидки, акции, новинки</MenuSub>
          </MenuContent>
          <MenuArrow><FiChevronRight size={20} /></MenuArrow>
        </MenuRow>
      </NeonCard>

      {/* Документы */}
      <SectionLabel>Документы</SectionLabel>
      <NeonCard>
        <MenuRow href="https://пс-стор.рф/oferta" target="_blank" onClick={haptic}>
          <MenuIconWrap $bg="rgba(255,255,255,0.08)" $color="rgba(255,255,255,0.6)">
            <FiFileText size={17} />
          </MenuIconWrap>
          <MenuContent>
            <MenuLabel style={{ color: 'rgba(255,255,255,0.65)', fontWeight: 500 }}>Пользовательское соглашение</MenuLabel>
          </MenuContent>
          <MenuArrow><FiChevronRight size={18} /></MenuArrow>
        </MenuRow>
        <MenuRow href="https://пс-стор.рф/privacy" target="_blank" onClick={haptic}>
          <MenuIconWrap $bg="rgba(255,255,255,0.08)" $color="rgba(255,255,255,0.6)">
            <FiLock size={17} />
          </MenuIconWrap>
          <MenuContent>
            <MenuLabel style={{ color: 'rgba(255,255,255,0.65)', fontWeight: 500 }}>Политика конфиденциальности</MenuLabel>
          </MenuContent>
          <MenuArrow><FiChevronRight size={18} /></MenuArrow>
        </MenuRow>
      </NeonCard>

      {copied && <CopiedToast>Ссылка скопирована!</CopiedToast>}

      {showCodesModal && (
        <ModalOverlay onClick={() => setShowCodesModal(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Резервные коды</ModalTitle>
              <ModalClose onClick={() => setShowCodesModal(false)}>&times;</ModalClose>
            </ModalHeader>
            <ModalBody>
              <CodeInput>
                <CodeInputField 
                  placeholder="Введите код..." 
                  value={newCode}
                  onChange={e => setNewCode(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && addCode()}
                  maxLength={50}
                />
                <AddCodeBtn onClick={addCode} disabled={!newCode.trim() || reserveCodes.length >= 10}>
                  <FiPlus size={16} />
                </AddCodeBtn>
              </CodeInput>
              
              <CodesList>
                {reserveCodes.map((code, idx) => (
                  <CodeItem key={idx}>
                    <CodeText>{code}</CodeText>
                    <DeleteCodeBtn onClick={() => removeCode(idx)}>
                      <FiX size={16} />
                    </DeleteCodeBtn>
                  </CodeItem>
                ))}
                {reserveCodes.length === 0 && (
                  <CodesHint>Добавьте резервные коды для быстрого оформления заказов</CodesHint>
                )}
              </CodesList>
              
              <CodesHint>
                {reserveCodes.length}/10 кодов • Коды сохраняются локально
              </CodesHint>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}

      {showHistoryModal && (
        <ModalOverlay onClick={() => setShowHistoryModal(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>История покупок</ModalTitle>
              <ModalClose onClick={() => setShowHistoryModal(false)}>&times;</ModalClose>
            </ModalHeader>
            <ModalBody>
              {purchaseHistory.length > 0 ? (
                purchaseHistory.map((item, idx) => (
                  <HistoryItem key={idx}>
                    <HistoryIcon $bg={item.status === 'success' ? 'rgba(52,199,89,0.15)' : 'rgba(255,214,10,0.15)'}>
                      {item.status === 'success' ? '✓' : '⏳'}
                    </HistoryIcon>
                    <HistoryInfo>
                      <HistoryTitle>{item.title}</HistoryTitle>
                      <HistoryDate>{item.date}</HistoryDate>
                    </HistoryInfo>
                    <div>
                      <HistoryPrice>{item.price.toLocaleString('ru')} ₽</HistoryPrice>
                      <HistoryStatus $color={item.status === 'success' ? '#34C759' : '#FFD60A'}>
                        {item.status === 'success' ? 'Выполнен' : 'В обработке'}
                      </HistoryStatus>
                    </div>
                  </HistoryItem>
                ))
              ) : (
                <EmptyHistory>
                  <div style={{fontSize: '40px', marginBottom: '12px'}}>🎮</div>
                  <div>Пока нет покупок</div>
                  <div style={{marginTop: '8px', fontSize: '12px'}}>Ваши заказы появятся здесь</div>
                </EmptyHistory>
              )}
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}

      {showSubscriptionModal && (
        <ModalOverlay onClick={() => setShowSubscriptionModal(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>PS Plus подписка</ModalTitle>
              <ModalClose onClick={() => setShowSubscriptionModal(false)}>&times;</ModalClose>
            </ModalHeader>
            <ModalBody>
              {isSubscriptionActive ? (
                <ActiveSubCard>
                  <div style={{fontSize: '48px'}}>⭐</div>
                  <SubName style={{fontSize: '20px', marginTop: '12px'}}>
                    PS Plus {subscription.tier === 'essential' ? 'Essential' : subscription.tier === 'extra' ? 'Extra' : 'Deluxe'}
                  </SubName>
                  <SubStatus $color={getDaysLeft() <= 3 ? '#FF3B30' : '#34C759'}>
                    {getDaysLeft() <= 1 ? '⚠️ Заканчивается завтра!' : `Осталось ${getDaysLeft()} дней`}
                  </SubStatus>
                  <SubDesc style={{marginTop: '8px'}}>
                    До {new Date(subscription.endDate).toLocaleDateString('ru')}
                  </SubDesc>
                  <CancelBtn onClick={cancelSubscription}>Отменить подписку</CancelBtn>
                </ActiveSubCard>
              ) : (
                <>
                  <SubDesc style={{marginBottom: '16px', textAlign: 'center'}}>
                    После оплаты подписки отметьте её здесь — бот напомнит о продлении
                  </SubDesc>
                  
                  <SubOption onClick={() => activateSubscription('essential', 1)}>
                    <SubIcon>🟡</SubIcon>
                    <SubInfo>
                      <SubName>Essential</SubName>
                      <SubDesc>1 месяц • Напомним через 29 дней</SubDesc>
                    </SubInfo>
                  </SubOption>
                  
                  <SubOption onClick={() => activateSubscription('essential', 3)}>
                    <SubIcon>🟡</SubIcon>
                    <SubInfo>
                      <SubName>Essential</SubName>
                      <SubDesc>3 месяца • Напомним через 89 дней</SubDesc>
                    </SubInfo>
                  </SubOption>
                  
                  <SubOption onClick={() => activateSubscription('essential', 12)}>
                    <SubIcon>🟡</SubIcon>
                    <SubInfo>
                      <SubName>Essential</SubName>
                      <SubDesc>12 месяцев • Напомним через 364 дня</SubDesc>
                    </SubInfo>
                  </SubOption>
                  
                  <div style={{height: '12px'}} />
                  
                  <SubOption onClick={() => activateSubscription('extra', 1)}>
                    <SubIcon>⚫</SubIcon>
                    <SubInfo>
                      <SubName>Extra</SubName>
                      <SubDesc>1 месяц • Напомним через 29 дней</SubDesc>
                    </SubInfo>
                  </SubOption>
                  
                  <SubOption onClick={() => activateSubscription('extra', 3)}>
                    <SubIcon>⚫</SubIcon>
                    <SubInfo>
                      <SubName>Extra</SubName>
                      <SubDesc>3 месяца • Напомним через 89 дней</SubDesc>
                    </SubInfo>
                  </SubOption>
                  
                  <SubOption onClick={() => activateSubscription('extra', 12)}>
                    <SubIcon>⚫</SubIcon>
                    <SubInfo>
                      <SubName>Extra</SubName>
                      <SubDesc>12 месяцев • Напомним через 364 дня</SubDesc>
                    </SubInfo>
                  </SubOption>
                  
                  <div style={{height: '12px'}} />
                  
                  <SubOption onClick={() => activateSubscription('deluxe', 1)}>
                    <SubIcon>🟣</SubIcon>
                    <SubInfo>
                      <SubName>Deluxe</SubName>
                      <SubDesc>1 месяц • Напомним через 29 дней</SubDesc>
                    </SubInfo>
                  </SubOption>
                  
                  <SubOption onClick={() => activateSubscription('deluxe', 3)}>
                    <SubIcon>🟣</SubIcon>
                    <SubInfo>
                      <SubName>Deluxe</SubName>
                      <SubDesc>3 месяца • Напомним через 89 дней</SubDesc>
                    </SubInfo>
                  </SubOption>
                  
                  <SubOption onClick={() => activateSubscription('deluxe', 12)}>
                    <SubIcon>🟣</SubIcon>
                    <SubInfo>
                      <SubName>Deluxe</SubName>
                      <SubDesc>12 месяцев • Напомним через 364 дня</SubDesc>
                    </SubInfo>
                  </SubOption>
                </>
              )}
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </Page>
  );
};

export default Profile;
